import {
  Component,
  Input,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Renderer2
} from '@angular/core';
import { PopoverDirective } from './popover.directive';

@Component({
  selector: 'vc-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss']
})
export class PopoverComponent implements AfterViewInit, OnDestroy {
  @Input()
  public content: string;

  @Input()
  public placement:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'auto'
    | 'auto top'
    | 'auto bottom'
    | 'auto left'
    | 'auto right' = 'top';

  @Input()
  public title: string;

  @Input()
  public animation = false;

  @Input()
  public closeOnClickOutside = false;

  @Input()
  public closeOnMouseOutside = false;

  @ViewChild('popoverRef', { static: false })
  private popoverRef: ElementRef;

  public popover: PopoverDirective;
  public onCloseFromOutside = new EventEmitter();
  public top = -10000;
  public left = -10000;
  public isIn = false;
  public displayType = 'none';
  public visibility = false;
  public effectivePlacement: string;

  /**
   * Closes dropdown if user clicks outside of this directive.
   */
  public onDocumentMouseDown(event: any) {
    const element = this.element.nativeElement;
    if (!element || !this.popover) {
      return;
    }
    if (element.contains(event.target) || this.popover.getElement().contains(event.target)) {
      return;
    }
    this.hide();
    this.onCloseFromOutside.emit(undefined);
  }

  constructor(protected element: ElementRef, protected cdr: ChangeDetectorRef, protected renderer: Renderer2) {}

  private listenClickFunc: any;
  private listenMouseFunc: any;

  ngAfterViewInit(): void {
    if (this.closeOnClickOutside) {
      this.listenClickFunc = this.renderer.listen('document', 'mousedown', (event: any) =>
        this.onDocumentMouseDown(event)
      );
    }
    if (this.closeOnMouseOutside) {
      this.listenMouseFunc = this.renderer.listen('document', 'mouseover', (event: any) =>
        this.onDocumentMouseDown(event)
      );
    }
    // Content is set only if we pass a string directly not a reference
    if (this.content) {
      this.show();
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.closeOnClickOutside) {
      this.listenClickFunc();
    }
    if (this.closeOnMouseOutside) {
      this.listenMouseFunc();
    }
  }

  public show(): void {
    if (!this.popover || !this.popover.getElement()) {
      return;
    }
    this.visibility = true;

    const p = this.positionElements(this.popover.getElement(), this.popoverRef.nativeElement, this.placement);
    this.displayType = 'block';
    this.top = p.top;
    this.left = p.left < 0 ? 0 : p.left;
    this.isIn = true;

    const isToSmall = p.left + this.popoverRef.nativeElement.offsetWidth >= window.innerWidth;
    if (isToSmall && this.effectivePlacement === 'right') {
      setTimeout(() => {
        const pp = this.positionElements(this.popover.getElement(), this.popoverRef.nativeElement, this.placement);
        this.top = pp.top;
        this.cdr.markForCheck();
      }, 1);
    }
  }

  public hide(): void {
    this.top = -10000;
    this.left = -10000;
    this.isIn = true;
    this.visibility = false;
    this.displayType = 'none';
    this.popover.hide();
  }

  public hideFromPopover() {
    this.top = -10000;
    this.left = -10000;
    this.isIn = true;
    this.visibility = false;
    this.displayType = 'none';
  }

  /**
   *
   *
   * @protected
   * @param {HTMLElement} hostEl
   * @param {HTMLElement} targetEl
   * @param {string} positionStr
   * @param {boolean} [appendToBody=false]
   * @returns {{ top: number; left: number }}
   */
  protected positionElements(
    hostEl: HTMLElement,
    targetEl: HTMLElement,
    positionStr: string,
    appendToBody: boolean = false
  ): { top: number; left: number } {
    const arrowWidth = 14;
    const positionStrParts = positionStr.split('-');
    let pos0 = positionStrParts[0];
    const pos1 = positionStrParts[1] || 'center';
    const hostElPos = appendToBody ? this.offset(hostEl) : this.position(hostEl);
    const targetElWidth = targetEl.offsetWidth;
    const targetElHeight = targetEl.offsetHeight;

    this.effectivePlacement = pos0 = this.getEffectivePlacement(pos0, hostEl, targetEl);
    this.effectivePlacement = `${pos0} ${pos1}`;

    const shiftWidth: any = {
      center: function(): number {
        return hostElPos.left + hostElPos.width / 2 - targetElWidth / 2;
      },
      left: function(): number {
        return hostElPos.left;
      },
      right: function(): number {
        return hostElPos.left + hostElPos.width;
      }
    };

    const shiftHeight: any = {
      center: function(): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top: function(): number {
        return hostElPos.top;
      },
      bottom: function(): number {
        return hostElPos.top + hostElPos.height;
      }
    };

    let targetElPos: { top: number; left: number };
    switch (pos0) {
      case 'right':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: shiftWidth[pos0]() + arrowWidth
        };
        break;

      case 'left':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: hostElPos.left - targetElWidth - arrowWidth
        };
        break;

      case 'bottom':
        targetElPos = {
          top: shiftHeight[pos0]() + arrowWidth,
          left: shiftWidth[pos1]()
        };
        break;

      default:
        targetElPos = {
          top: hostElPos.top - targetElHeight - arrowWidth,
          left: shiftWidth[pos1]()
        };
        break;
    }

    return targetElPos;
  }

  /**
   *
   *
   * @protected
   * @param {HTMLElement} nativeEl
   * @returns {{ width: number; height: number; top: number; left: number }}
   * @memberof PopoverComponent
   */
  protected position(nativeEl: HTMLElement): { width: number; height: number; top: number; left: number } {
    let offsetParentBCR = { top: 0, left: 0 };
    const elBCR = this.offset(nativeEl);
    const offsetParentEl = this.parentOffsetEl(nativeEl);
    if (offsetParentEl !== window.document) {
      offsetParentBCR = this.offset(offsetParentEl);
      offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left
    };
  }
  /**
   *
   *
   * @protected
   * @param {*} nativeEl
   * @returns {{ width: number; height: number; top: number; left: number }}
   * @memberof PopoverComponent
   */
  protected offset(nativeEl: any): { width: number; height: number; top: number; left: number } {
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: boundingClientRect.top + (window.pageYOffset || window.document.documentElement.scrollTop),
      left: boundingClientRect.left + (window.pageXOffset || window.document.documentElement.scrollLeft)
    };
  }

  /**
   *
   *
   * @protected
   * @param {HTMLElement} nativeEl
   * @param {string} cssProp
   * @returns {string}
   * @memberof PopoverComponent
   */
  protected getStyle(nativeEl: HTMLElement, cssProp: string): string {
    if ((nativeEl as any).currentStyle) {
      // IE
      return (nativeEl as any).currentStyle[cssProp];
    }

    if (window.getComputedStyle) {
      return (window.getComputedStyle as any)(nativeEl)[cssProp];
    }

    // finally try and get inline style
    return (nativeEl.style as any)[cssProp];
  }

  /**
   *
   *
   * @protected
   * @param {HTMLElement} nativeEl
   * @returns {boolean}
   * @memberof PopoverComponent
   */
  protected isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
  }

  /**
   *
   *
   * @protected
   * @param {HTMLElement} nativeEl
   * @returns {*}
   * @memberof PopoverComponent
   */
  protected parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || window.document;
    while (offsetParent && offsetParent !== window.document && this.isStaticPositioned(offsetParent)) {
      offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || window.document;
  }

  /**
   *
   *
   * @protected
   * @param {string} placement
   * @param {HTMLElement} hostElement
   * @param {HTMLElement} targetElement
   * @returns {string}
   * @memberof PopoverComponent
   */
  protected getEffectivePlacement(placement: string, hostElement: HTMLElement, targetElement: HTMLElement): string {
    const placementParts = placement.split(' ');
    if (placementParts[0] !== 'auto') {
      return placement;
    }

    const hostElBoundingRect = hostElement.getBoundingClientRect();

    const desiredPlacement = placementParts[1] || 'bottom';

    if (desiredPlacement === 'top' && hostElBoundingRect.top - targetElement.offsetHeight < 0) {
      return 'bottom';
    }
    if (desiredPlacement === 'bottom' && hostElBoundingRect.bottom + targetElement.offsetHeight > window.innerHeight) {
      return 'top';
    }
    if (desiredPlacement === 'left' && hostElBoundingRect.left - targetElement.offsetWidth < 0) {
      return 'right';
    }
    if (desiredPlacement === 'right' && hostElBoundingRect.right + targetElement.offsetWidth > window.innerWidth) {
      return 'left';
    }

    return desiredPlacement;
  }
}
